import { ReactElement, useEffect } from "react";
import styles from "./LeadFormPopup.module.css";
import type {
    LandingPageTemplateResponse,
    SegmentComponent,
} from "@/types/landing";
import { logNewGoogleAnalyticsEvent } from "src/utils/utils";
import { startSpin } from "@/components/landing/Wheel";
import { SpinStartEnd } from "@/components/Spinner/index";
import { DynamicForm } from "../DynamicForm";
import type { Site } from "@/types/sites";
import { Terms } from "../Terms/index";
import Image from "next/image";
import { UserCookie } from "@/types/nextjs";

type LeadFormPopup = ({
    winningSegment,
    site,
    landingPage,
    isModalOpen,
    setIsModalOpen,
    spinStartEnd,
    useBannerflow,
    onOfferPage,
    setLoaderIsOpen,
}: {
    winningSegment: SegmentComponent | undefined;
    site: Site | null;
    landingPage: LandingPageTemplateResponse | null;
    isModalOpen: boolean;
    setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    spinStartEnd: SpinStartEnd;
    useBannerflow?: boolean;
    onOfferPage?: boolean;
    setLoaderIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
    userCookie: UserCookie;
}) => ReactElement;

export const LeadFormPopup: LeadFormPopup = ({
    winningSegment,
    site,
    landingPage,
    isModalOpen,
    setIsModalOpen,
    spinStartEnd,
    useBannerflow = false,
    onOfferPage = false,
    setLoaderIsOpen,
    userCookie
}) => {
    const {
        title = "",
        subtitle = "",
        heading = "",
    } = landingPage?.data?.attributes.sanitisedAUJ?.templateVars ?? landingPage?.data?.attributes ?? {};

    const useAuj = landingPage?.data?.attributes.sanitisedAUJ?.templateVars != null;
    
    useEffect(() => {
        if (isModalOpen && useBannerflow) {
            logNewGoogleAnalyticsEvent("lead-popup-open");
        }
    }, [isModalOpen]);

    // If we're using bannerflow, it has its own spinner.
    // If we're on an offer page, there is no spinner
    const spinnerExists = !(useBannerflow || onOfferPage);

    return (<>
        {spinnerExists ? (
            <button
                onClick={() => {
                    startSpin(setIsModalOpen, spinStartEnd);
                }}
                className={styles.btnOpen}
            >
                SPIN THE WHEEL
            </button>
        ) : null}
        {isModalOpen ? (
            <div id={styles.spinnerModal} className={styles.modal}>
                <div className={styles.modalContent}>
                    <div className={styles.formContainer}>
                        {onOfferPage ? (
                            <Image
                                className={styles.closeButton}
                                onClick={() => {
                                    setIsModalOpen(false);
                                }}
                                alt="Close"
                                width="25"
                                height="25"
                                src={"/Close.svg"}
                                style={{
                                    maxWidth: "100%",
                                    height: "auto"
                                }}></Image>
                        ) : null}
                        <Image
                            src="/Unlock.gif"
                            alt=""
                            width="70"
                            height="70"
                            style={{
                                maxWidth: "100%",
                                height: "auto"
                            }} />
                        <div className={styles.youUnlocked}>
                            {useBannerflow && title
                                ? title
                                : "YOU'VE UNLOCKED"}
                        </div>
                        <div className={styles.prizeText}>
                            {(!spinnerExists && subtitle) || useAuj
                                ? subtitle
                                : // If we are using a spinner then the winning segment will have
                                  // the prize text
                                  winningSegment?.prizeText}
                        </div>
                        <div className={styles.muchMore}>
                            {useBannerflow || useAuj ? heading : "& MUCH MORE..."}
                        </div>
                        <DynamicForm
                            site={site}
                            landingPage={landingPage}
                            fromPopup={true}
                            setIsModalOpen={setIsModalOpen}
                            onOfferPage={onOfferPage}
                            setLoaderIsOpen={setLoaderIsOpen}
                            userCookie={userCookie}
                        />
                    </div>
                    <div className={styles.termsContainer}>
                        <Terms
                            standalone={true}
                            termsAndConditionsText={
                                landingPage?.data?.attributes
                                    ?.sanitisedTermsAndConditionsText || ""
                            }
                        />
                    </div>
                </div>
            </div>
        ) : null}
    </>);
};
